import type { ReactNode } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, type DialogProps, type SxProps, type Theme } from '@mui/material';
import { CloseIcon } from '../../icons';
import { hf, hfAlpha } from '../../theme';
import { composeSx } from '../../utils/composeSx';

export type HFModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  dividers?: boolean;
  contentSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  disableBackdropClose?: boolean;
};

export function HFModal({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  dividers = false,
  contentSx,
  titleSx,
  actionsSx,
  disableBackdropClose = false,
}: HFModalProps) {
  if (open && typeof document !== 'undefined') {
    const active = document.activeElement;
    if (active instanceof HTMLElement && document.getElementById('root')?.contains(active)) active.blur();
  }
  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: hfAlpha(hf.navy900, 0.72),
            backdropFilter: 'blur(10px)',
          },
        },
        paper: {
          sx: {
            borderRadius: 2.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            boxShadow: `0 24px 64px ${hfAlpha(hf.navy900, 0.5)}`,
            overflow: 'hidden',
          },
        },
      }}
    >
      {title != null ? (
        <DialogTitle
          sx={composeSx(
            {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 3,
              py: 2,
            },
            titleSx,
          )}
        >
          <Typography
            component="span"
            sx={{
              m: 0,
              pl: 1.125,
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              color: 'text.primary',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{
              flexShrink: 0,
              border: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main', color: 'text.primary' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
      ) : null}
      <DialogContent
        dividers={dividers}
        sx={composeSx(
          {
            px: 3,
            pt: title != null ? 2 : 3,
            pb: actions != null ? 2 : 3,
            color: 'text.secondary',
            fontSize: 14,
            lineHeight: 1.5,
          },
          contentSx,
        )}
      >
        {children}
      </DialogContent>
      {actions != null ? <DialogActions sx={composeSx({ px: 3, pb: 3, pt: 0, gap: 1 }, actionsSx)}>{actions}</DialogActions> : null}
    </Dialog>
  );
}
